import { Component, Inject, OnInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { FormsService } from 'src/app/applicationModules/eSignature/services/forms.service';
import { DocPreviewDialogComponent } from 'src/app/middleware/shared/common/docPreview/doc-preview-dialog.component';

const SERVICE_TYPE_NAMES: { [key: string]: string } = {
  '1': 'Medical Care', '2': 'Surgical', '3': 'Consultation',
  '12': 'Dental / Oral Cavity', '23': 'Diagnostic X-Ray', '24': 'Diagnostic Lab',
  '25': 'X-Ray Therapy', '26': 'Dialysis', '27': 'Audiological Exam',
  '28': 'Hearing Coverage', '30': 'Health Benefit Plan', '33': 'Chiropractic',
  '35': 'Dental Care', '36': 'Optical', '37': 'Optical - Exam',
  '38': 'Psychotherapy', '39': 'Psychiatric - Outpatient', '40': 'Physical Therapy',
  '42': 'Home Health Care', '45': 'Hospice', '47': 'Hospital - Inpatient',
  '48': 'Hospital - Inpatient', '49': 'Ambulatory Surgical', '50': 'Hospital - Outpatient',
  '51': 'Occupational Therapy', '52': 'CT Scan', '53': 'Renal Supplies',
  '67': 'Inhalation Therapy', '69': 'Mastectomy Services', '73': 'Diagnostic Medical',
  '76': 'Dialysis', '83': 'Infusion Therapy', '86': 'Emergency Services',
  '88': 'Pharmacy', '98': 'Physician Visit - Office',
  'A4': 'Psychiatric', 'A5': 'Substance Abuse', 'A6': 'Occupational Therapy',
  'A7': 'Psychiatric - Inpatient', 'A8': 'Cardiac Rehabilitation',
  'AG': 'Primary Care (Physician)', 'AJ': 'Skilled Nursing Care', 'AK': 'Home Health Aide',
  'AI': 'Home Health Visits', 'AL': 'Vision (Optometry)',
  'BT': 'Radiation Therapy', 'BU': 'Blood', 'BV': 'Surgical',
  'DM': 'Dental Coverage', 'MH': 'Mental Health', 'UC': 'Urgent Care',
  'MC': 'Medicaid', 'MA': 'Medicare Part A', 'MB': 'Medicare Part B',
  'C1': 'Commercial Insurance', 'OT': 'Other Insurance'
};

@Component({
  selector: 'app-waystar-verification',
  templateUrl: './waystar-verification.component.html',
  styleUrls: ['./waystar-verification.component.scss']
})
export class WaystarVerificationComponent implements OnInit {
  activeSection: string = 'Intake Data';
  // Coverage Detection is hidden (Coverage Discovery is disabled); Manual Call
  // Workflow + Authorization are parked at the bottom (under construction).
  sections: string[] = [
    'Intake Data',
    'Payer Stack',
    'Electronic Verification',
    'Financial Summary',
    'Coverage Details',
    'Verification History',
    'Notes & Audit Trail',
    'Manual Call Workflow',
    'Authorization'
  ];

  intakeForm: FormGroup;
  payers: any[] = [];
  filteredPayers: any[] = [];

  private readonly TEST_PAYERS: { id: string; name: string; isTest: boolean }[] = [
    { id: '33333', name: 'Medicare test payer',   isTest: true },
    { id: '55555', name: 'Medicaid test payer',   isTest: true },
    { id: '66666', name: 'Commercial test payer', isTest: true }
  ];

  // Test payers are for QA on staging/local only. On production
  // (app.flowtrics.com) they must never appear. Detected by hostname so the
  // same build behaves correctly wherever it is served.
  private get includeTestPayers(): boolean {
    const host = ((typeof window !== 'undefined' && window.location && window.location.hostname) || '').toLowerCase();
    return host !== 'app.flowtrics.com';
  }

  waystarResponse: any = null;
  isLoading: boolean = false;
  activeEvTab: string = 'summary';

  // ─── Intake Data split view (AI-extracted reference + source document) ───────
  aiDocUrl: string = '';
  aiDocName: string = 'Source Document';
  aiDataFields: { fieldName: string; value: string }[] = [];
  isLoadingAiData: boolean = false;
  // Source document opens full-screen over the dialog via a top-bar icon.
  showDocViewer: boolean = false;

  // Key Value tab — raw OCR key/value pairs, same source as the AI tool's
  // Key Value Mapping popup (GetOCRJSonDataByURL → allKeyValueData).
  activeAiTab: 'ai' | 'kv' = 'ai';
  keyValueData: { key: string; value: string }[] = [];
  isLoadingKeyValues: boolean = false;
  kvSearch: string = '';
  private keyValuesLoaded = false;

  // ─── Verification History ────────────────────────────────────────────────────
  historyRecords: any[] = [];
  isLoadingHistory: boolean = false;
  selectedHistoryRecord: any | null = null;

  // ─── Acceptance Status ───────────────────────────────────────────────────────
  // null=not run, 1=pending, 2=accepted, 3=rejected
  acceptanceStatus: number | null = null;
  isUpdatingAcceptance: boolean = false;

  // ─── Coverage Detection ──────────────────────────────────────────────────────
  coverageDetectionResults: any = null;
  isDetectingCoverage: boolean = false;
  showCoverageAdvanced: boolean = false;

  // Cost-aware confirm modal: when a payer is already selected, force a
  // deliberate choice before running the $4.50 Coverage Discovery call.
  showCoverageCostConfirm: boolean = false;
  coverageConfirmReason: string = '';

  // ─── Provider on File (office-level default) ────────────────────────────────
  // One record per office (ECM_WaystarProviderOnFiles). null = nothing on file
  // yet; the card then shows "--" with an inline Add action.
  providerOnFile: { providerName: string; providerNpi: string; providerTaxId: string; pin: string } | null = null;
  isEditingProviderOnFile = false;
  isSavingProviderOnFile = false;
  private providerEditSnapshot: { providerName: string; providerNpi: string; providerTaxId: string; providerPin: string } | null = null;

  // ─── New intake layout toggles ───────────────────────────────────────────────
  // Optional patient fields are collapsed behind "View more patient details".
  showOptionalDetails = false;
  // Coverage Discovery UI ("Insurance unknown?" fallback) is hidden per current
  // requirements — markup and logic stay intact so it can be re-enabled.
  showCoverageDiscovery = false;

  // ─── Notes & Audit Trail ─────────────────────────────────────────────────────
  notes: any[] = [];
  isLoadingNotes: boolean = false;
  isSubmittingNote: boolean = false;
  newNoteText: string = '';
  readonly NOTE_MAX_LENGTH = 2000;

  @ViewChild('payerSearchInput') payerSearchInput!: ElementRef;
  @ViewChild('payerPickerInput') payerPickerInput!: ElementRef;

  // Full-screen payer picker (search + chip results) opened from the payer field.
  showPayerPicker = false;

  constructor(
    public dialogRef: MatDialogRef<WaystarVerificationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private http: HttpClient,
    private toastr: ToastrService,
    private formsService: FormsService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer
  ) {
    // Mirrors the API-side validation in SF1Builder.Validate: the required set is
    // the HIPAA minimum search criteria; NPI must be 10 digits, Tax ID is optional
    // but must be a 9-digit EIN when supplied.
    this.intakeForm = this.fb.group({
      payerId:              [null, Validators.required],
      providerName:         ['', Validators.required],
      providerNpi:          ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      providerTaxId:        ['', Validators.pattern(/^\d{9}$/)],
      // Provider PIN — display/reference only; stored on Provider on File but
      // never included in any Waystar API request.
      providerPin:          [''],
      // How the provider is sent to the payer on Run:
      //   'npi'      → Name + NPI (default; Tax ID not sent)
      //   'npi_tax'  → Name + NPI + Tax ID
      //   'name_tax' → Name + Tax ID (NPI omitted)
      providerIdMode:       ['npi'],
      subscriberId:         ['', Validators.required],
      subscriberFirstName:  ['', Validators.required],
      subscriberMiddleName: [''],
      subscriberLastName:   ['', Validators.required],
      dateOfBirth:          ['', Validators.required],
      address:              [''],
      city:                 [''],
      state:                ['', Validators.pattern(/^[A-Za-z]{2}$/)],
      zip:                  ['', Validators.pattern(/^\d{5}(-\d{4})?$/)],
      gender:               [''],
      ssn:                  ['', Validators.pattern(/^(\d{9}|\d{3}-\d{2}-\d{4})$/)],
      medicalRecordNumber:  ['']
    });
  }

  ngOnInit(): void {
    this.loadAiDocumentData();
    this.loadProviderOnFile();
    this.acceptanceStatus = this.data?.selectedRecord?.waystarAcceptanceStatus ?? null;
    if (this.data?.selectedRecord?.waystarCoverageDetectionJson) {
      try { this.coverageDetectionResults = JSON.parse(this.data.selectedRecord.waystarCoverageDetectionJson); } catch { }
    }
    const testPayers = this.includeTestPayers ? this.TEST_PAYERS : [];
    this.http.get<any[]>('assets/json/ViewPayers.json').subscribe(res => {
      const realPayers = (res || []).filter(p => !this.TEST_PAYERS.find(t => t.id === p.id));
      this.payers = [...testPayers, ...realPayers];
      this.indexPayers();
      this.applyPayerFilter();
      this.autoPopulateForm();
      this.hydrateSavedVerification();
    }, err => {
      console.error('Failed to load ViewPayers.json', err);
      this.payers = [...testPayers];
      this.indexPayers();
      this.applyPayerFilter();
      this.autoPopulateForm();
      this.hydrateSavedVerification();
    });
  }

  // Loads the referral's source document URL + AI-extracted field/value pairs
  // (the same data behind the AI Tool's "AI Data" tab) so they can be shown as
  // read-only reference beside the intake form. No-op when opened without a
  // documentID (e.g. from IV / Core paths that don't carry one).
  private loadAiDocumentData(): void {
    const documentID = this.data?.documentID;
    if (!documentID) return;

    this.aiDocUrl = this.data?.pdfUrl || '';
    this.aiDocName = this.data?.selectedRecord?.documentName || 'Source Document';
    this.isLoadingAiData = true;

    const req = {
      fileManagerItemID: documentID,
      userID:            this.data?.userID,
      organizationID:    this.data?.organizationID,
      databaseID:        this.data?.databaseID,
      officeID:          this.data?.officeID
    };

    this.formsService.GetFileManagerFileDetail(req).subscribe({
      next: (res: any) => {
        this.isLoadingAiData = false;
        const d = res?.data;
        if (!d) return;
        if (d.azureblobPath) this.aiDocUrl = d.azureblobPath;
        if (d.fileName || d.documentName) this.aiDocName = d.fileName || d.documentName;
        if (d.documentData) {
          try {
            const parsed = JSON.parse(d.documentData) || [];
            this.aiDataFields = parsed
              .filter((e: any) => e && e.FieldName)
              .map((e: any) => ({ fieldName: e.FieldName, value: e.Value ?? '' }));
          } catch { this.aiDataFields = []; }
        }
      },
      error: () => { this.isLoadingAiData = false; }
    });
  }

  selectAiTab(tab: 'ai' | 'kv'): void {
    this.activeAiTab = tab;
    if (tab === 'kv' && !this.keyValuesLoaded) this.loadKeyValueData();
  }

  // Fetches the raw OCR key/value pairs behind the AI tool's Key Value Mapping
  // popup. Lazy — only on first open of the tab, since the OCR JSON can be large.
  private loadKeyValueData(): void {
    const documentID = this.data?.documentID;
    if (!documentID) return;
    this.keyValuesLoaded = true;
    this.isLoadingKeyValues = true;

    const req = {
      fileManagerItemID: documentID,
      userID:            this.data?.userID,
      organizationID:    this.data?.organizationID,
      databaseID:        this.data?.databaseID,
      officeID:          this.data?.officeID
    };

    this.formsService.GetOCRJSonDataByURL(req).subscribe({
      next: (res: any) => {
        this.isLoadingKeyValues = false;
        const list = res?.data?.allKeyValueData || [];
        this.keyValueData = list
          .filter((e: any) => e && (e.key || e.value))
          .map((e: any) => ({ key: (e.key ?? '').toString().trim(), value: (e.value ?? '').toString().trim() }));
      },
      error: () => { this.isLoadingKeyValues = false; }
    });
  }

  onKvSearch(event: Event): void {
    this.kvSearch = (event.target as HTMLInputElement).value || '';
  }

  filteredKeyValues(): { key: string; value: string }[] {
    const q = this.kvSearch.trim().toLowerCase();
    if (!q) return this.keyValueData;
    return this.keyValueData.filter(e =>
      e.key.toLowerCase().includes(q) || e.value.toLowerCase().includes(q));
  }

  // True when there is a source document / AI data to show the split view for.
  get hasAiDoc(): boolean {
    return !!this.aiDocUrl || this.aiDataFields.length > 0;
  }

  // Esc closes the full-screen document overlay (the dialog itself has
  // disableClose, so Esc is free to use here).
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.showPayerPicker) { this.showPayerPicker = false; return; }
    if (this.showDocViewer) this.showDocViewer = false;
  }

  copyAiValue(value: string): void {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      this.toastr.success('Value copied to clipboard.', 'Copied');
    });
  }

  // Opens the source document in a near-full-screen viewer and auto-finds +
  // highlights every occurrence of this AI-extracted value, with prev/next
  // match navigation (Enter / Shift+Enter or the header buttons).
  openAiValueInDoc(field: { fieldName: string; value: string }): void {
    if (!this.aiDocUrl) {
      this.toastr.info('No source document is available to search.', 'Find in document');
      return;
    }
    if (!field?.value) return;
    this.dialog.open(DocPreviewDialogComponent, {
      data: {
        url: this.aiDocUrl,
        name: this.aiDocName,
        searchTerm: field.value,
        contextLabel: 'Finding: ' + field.fieldName
      },
      width: '96vw',
      maxWidth: '96vw',
      height: '94vh',
      panelClass: 'doc-preview-panel',
      backdropClass: 'doc-preview-backdrop',
      autoFocus: false,
      restoreFocus: true
    });
  }

  // Patches a saved intake snapshot into the form, skipping empty values so a
  // snapshot taken before a field existed (or was left blank) doesn't wipe out
  // what autoPopulateForm just filled from the record — e.g. reopening the
  // dialog after a run without address must not clear the address fields.
  private patchIntakeNonEmpty(saved: any): void {
    if (!saved || typeof saved !== 'object') return;

    // Legacy snapshots (saved before the address split) hold the whole
    // concatenated address in `address` with no city/state/zip. Split it the
    // same way autoPopulateForm does, instead of restoring it into Street.
    if (saved.address && !saved.city && !saved.state && !saved.zip) {
      const addr = this.parseAddress(saved.address);
      saved = { ...saved, address: addr.street, city: addr.city, state: addr.state, zip: addr.zip };
    }

    const nonEmpty: any = {};
    for (const key of Object.keys(saved)) {
      const v = saved[key];
      if (v !== null && v !== undefined && v !== '') nonEmpty[key] = v;
    }
    this.intakeForm.patchValue(nonEmpty);
  }

  private hydrateSavedVerification() {
    const rec = this.data?.selectedRecord;
    if (rec) {
      if (rec.waystarIntakeJson) {
        try {
          this.patchIntakeNonEmpty(JSON.parse(rec.waystarIntakeJson));
          this.applyProviderOnFileToForm();
        } catch { /* corrupt JSON — fall back to autoPopulateForm result */ }
      }
      if (rec.waystarResponseJson) {
        try {
          this.waystarResponse = JSON.parse(rec.waystarResponseJson);
          this.updateFailureState(this.waystarResponse);
          this.activeSection = 'Electronic Verification';
          this.activeEvTab = 'summary';
        } catch { /* ignore */ }
      }
    }
    // Unified-table hydration: regardless of caller (IV / Referral / Core),
    // pull the latest verification for the case from ECM_WaystarVerifications
    // and let it overwrite anything that came in on selectedRecord.
    this.hydrateFromUnifiedTable();
  }

  private hydrateFromUnifiedTable(): void {
    if (!this.hasCaseRef()) return;
    this.formsService.getWaystarVerificationHistoryV2(this.buildCaseRef()).subscribe({
      next: (res) => {
        const records: any[] = res?.data?.records || [];
        if (!records.length) return;

        // Records arrive ordered by VerifiedOn DESC — first row is latest.
        const latest = records[0];
        const intakeJson           = latest.intakeJson           ?? latest.IntakeJson;
        const responseJson         = latest.responseJson         ?? latest.ResponseJson;
        const coverageJson         = latest.coverageDetectionJson ?? latest.CoverageDetectionJson;
        const acceptanceStatus     = latest.acceptanceStatus     ?? latest.AcceptanceStatus;

        if (intakeJson) {
          try {
            this.patchIntakeNonEmpty(JSON.parse(intakeJson));
            this.applyProviderOnFileToForm();
          } catch { /* ignore */ }
        }
        if (responseJson) {
          try {
            this.waystarResponse = JSON.parse(responseJson);
            this.updateFailureState(this.waystarResponse);
            this.activeSection = 'Electronic Verification';
            this.activeEvTab   = 'summary';
          } catch { /* ignore */ }
        }
        if (coverageJson) {
          try { this.coverageDetectionResults = JSON.parse(coverageJson); } catch { /* ignore */ }
        }
        if (acceptanceStatus !== null && acceptanceStatus !== undefined) {
          this.acceptanceStatus = acceptanceStatus;
        }
      },
      error: () => { /* silent — first-time-open case may simply have no history */ }
    });
  }

  // ─── Provider on File (office-level default) ────────────────────────────────

  private buildProviderOnFileReq(): any {
    return {
      organizationID: this.data?.organizationID,
      officeID:       this.data?.officeID,
      userID:         this.data?.userID,
      databaseID:     this.data?.databaseID
    };
  }

  private loadProviderOnFile(): void {
    if (!this.data?.organizationID || !this.data?.officeID) return;
    this.formsService.getWaystarProviderOnFileV2(this.buildProviderOnFileReq()).subscribe({
      next: (res) => {
        const d = res?.data;
        if (d?.exists) {
          this.providerOnFile = {
            providerName:  d.providerName  || '',
            providerNpi:   d.providerNpi   || '',
            providerTaxId: d.providerTaxId || '',
            pin:           d.pin           || ''
          };
          this.applyProviderOnFileToForm();
        }
      },
      error: () => { /* silent — office may simply have no provider on file yet */ }
    });
  }

  // The office default is authoritative for the provider fields — it wins over
  // values auto-filled from the record (locationName) or restored from a saved
  // intake snapshot, both of which may patch these controls asynchronously.
  private applyProviderOnFileToForm(): void {
    if (!this.providerOnFile || this.isEditingProviderOnFile) return;
    this.intakeForm.patchValue({
      providerName:  this.providerOnFile.providerName,
      providerNpi:   this.providerOnFile.providerNpi,
      providerTaxId: this.providerOnFile.providerTaxId,
      providerPin:   this.providerOnFile.pin
    });
  }

  hasProviderDetails(): boolean {
    return !!(this.intakeForm.get('providerName')?.value ||
              this.intakeForm.get('providerNpi')?.value ||
              this.intakeForm.get('providerTaxId')?.value);
  }

  editProviderOnFile(): void {
    this.providerEditSnapshot = {
      providerName:  this.intakeForm.get('providerName')?.value  || '',
      providerNpi:   this.intakeForm.get('providerNpi')?.value   || '',
      providerTaxId: this.intakeForm.get('providerTaxId')?.value || '',
      providerPin:   this.intakeForm.get('providerPin')?.value   || ''
    };
    this.isEditingProviderOnFile = true;
  }

  cancelProviderOnFileEdit(): void {
    if (this.providerEditSnapshot) this.intakeForm.patchValue(this.providerEditSnapshot);
    this.providerEditSnapshot = null;
    this.isEditingProviderOnFile = false;
  }

  saveProviderOnFile(): void {
    const name  = (this.intakeForm.get('providerName')?.value  || '').toString().trim();
    const npi   = (this.intakeForm.get('providerNpi')?.value   || '').toString().trim();
    const taxId = (this.intakeForm.get('providerTaxId')?.value || '').toString().trim();
    const pin   = (this.intakeForm.get('providerPin')?.value   || '').toString().trim();

    if (!name) { this.toastr.warning('Provider Name is required.', 'Provider on File'); return; }
    if (!/^\d{10}$/.test(npi)) { this.toastr.warning('NPI must be exactly 10 digits.', 'Provider on File'); return; }
    if (taxId && !/^\d{9}$/.test(taxId)) { this.toastr.warning('Tax ID must be exactly 9 digits.', 'Provider on File'); return; }
    if (!this.data?.organizationID || !this.data?.officeID) {
      this.toastr.warning('No office context available — cannot save.', 'Provider on File');
      return;
    }

    this.isSavingProviderOnFile = true;
    const req = { ...this.buildProviderOnFileReq(), providerName: name, providerNpi: npi, providerTaxId: taxId, pin };
    this.formsService.saveWaystarProviderOnFileV2(req).subscribe({
      next: (res) => {
        this.isSavingProviderOnFile = false;
        const d = res?.data;
        if (d?.exists) {
          this.providerOnFile = {
            providerName:  d.providerName  || '',
            providerNpi:   d.providerNpi   || '',
            providerTaxId: d.providerTaxId || '',
            pin:           d.pin           || ''
          };
          this.isEditingProviderOnFile = false;
          this.providerEditSnapshot = null;
          this.toastr.success('Provider on file saved for this office.', 'Saved');
        } else {
          this.toastr.error('Failed to save provider on file.', 'Error');
        }
      },
      error: () => {
        this.isSavingProviderOnFile = false;
        this.toastr.error('Failed to save provider on file.', 'Error');
      }
    });
  }

  autoPopulateForm() {
    if (this.data?.selectedRecord) {
      const row = this.data.selectedRecord;
      // Prefer discrete name fields when the record carries them; otherwise
      // split the single `name` string. Discrete values always win.
      const parsedName = this.parseName(row.name);
      const firstName  = row.firstName  || parsedName.first;
      const middleName = row.middleName || parsedName.middle;
      const lastName   = row.lastName   || parsedName.last;
      // Prefer the real DOB (Date instance for the datepicker); fall back to
      // an age-derived year only when no DOB is available.
      let dob: any = '';
      const rawDob = row.dateOfBirth || row.dob;
      if (rawDob) {
        const d = new Date(rawDob);
        if (!isNaN(d.getTime())) dob = d;
      } else if (row.age) {
        const year = new Date().getFullYear() - parseInt(row.age, 10);
        dob = new Date(`${year}-01-01`);
      }
      let matchedPayerId = null;
      if (row.payer && this.payers.length > 0) {
        const found = this.payers.find(p =>
          p.name.toLowerCase().includes(row.payer.toLowerCase()) ||
          row.payer.toLowerCase().includes(p.name.toLowerCase())
        );
        if (found) matchedPayerId = found.id;
      }
      // The record usually carries one concatenated address string; split it so
      // street goes to N3 and city/state/zip go to N4. Record-level discrete
      // fields win over anything parsed out of the string.
      const addr = this.parseAddress(row.address);
      this.intakeForm.patchValue({
        subscriberFirstName: firstName,
        subscriberMiddleName: middleName,
        subscriberLastName:  lastName,
        gender:              this.normalizeGender(row.gender || row.sex),
        dateOfBirth:         dob,
        address:             addr.street,
        city:                row.city  || addr.city,
        state:               row.state || addr.state,
        zip:                 row.zip   || row.zipCode || addr.zip,
        ssn:                 this.cleanSsn(row.ssn),
        medicalRecordNumber: row.mrn || row.medicalRecordNumber || '',
        payerId:             matchedPayerId,
        providerName:        row.locationName || ''
      });
    }
    // Office provider-on-file (if already loaded) beats the locationName autofill.
    this.applyProviderOnFileToForm();
  }

  // ── Input format guards ──────────────────────────────────────────────────
  // Waystar is strict about numeric formats (NPI 10 digits, EIN 9 digits,
  // SSN 123-45-6789 or 123456789, ZIP 5 or 5+4). Guide the user by
  // construction: strip invalid characters as they type and auto-insert the
  // expected separators, so a wrong format can't be entered in the first place.

  sanitizeDigits(controlName: string, maxLen: number): void {
    const ctrl = this.intakeForm.get(controlName);
    if (!ctrl) return;
    const clean = (ctrl.value ?? '').toString().replace(/\D/g, '').slice(0, maxLen);
    if (clean !== ctrl.value) ctrl.setValue(clean);
  }

  // Auto-formats to 123-45-6789 as the user types digits.
  formatSsnInput(): void {
    const ctrl = this.intakeForm.get('ssn');
    if (!ctrl) return;
    const d = (ctrl.value ?? '').toString().replace(/\D/g, '').slice(0, 9);
    const out = d.length > 5 ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`
              : d.length > 3 ? `${d.slice(0, 3)}-${d.slice(3)}`
              : d;
    if (out !== ctrl.value) ctrl.setValue(out);
  }

  // Auto-formats to 40212 or 40212-1234 (ZIP+4) as the user types digits.
  formatZipInput(): void {
    const ctrl = this.intakeForm.get('zip');
    if (!ctrl) return;
    const d = (ctrl.value ?? '').toString().replace(/\D/g, '').slice(0, 9);
    const out = d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
    if (out !== ctrl.value) ctrl.setValue(out);
  }

  // Letters only, uppercased, max 2 (USPS state code).
  formatStateInput(): void {
    const ctrl = this.intakeForm.get('state');
    if (!ctrl) return;
    const clean = (ctrl.value ?? '').toString().replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 2);
    if (clean !== ctrl.value) ctrl.setValue(clean);
  }

  // Member IDs and MRNs never contain spaces — strip them so copy/paste from
  // cards or PDFs (which often brings a trailing space) can't corrupt the
  // inquiry or the REF*EA match-back.
  sanitizeNoSpaces(controlName: string): void {
    const ctrl = this.intakeForm.get(controlName);
    if (!ctrl) return;
    const clean = (ctrl.value ?? '').toString().replace(/\s/g, '');
    if (clean !== ctrl.value) ctrl.setValue(clean);
  }

  // Source systems use all-zero SSNs (000-00-0000, 00000000, …) as a
  // "not provided" placeholder — never prefill those into the intake, since
  // sending them to Waystar as REF*SY would hurt matching instead of helping.
  private cleanSsn(raw: any): string {
    const text = (raw || '').toString().trim();
    if (!text) return '';
    // Masked SSNs from source systems (e.g. XXX-XX-1234, ***-**-6789) are not
    // real numbers — never prefill them, and never send them to Waystar.
    if (/[xX*]/.test(text)) return '';
    const digits = text.replace(/[-\s]/g, '');
    if (/^0+$/.test(digits)) return '';
    return text;
  }

  // Splits a single name string into first / middle / last. Handles:
  //   "DUNCAN, SUSAN RENEE"  (Last, First Middle — comma format)
  //   "SUSAN RENEE DUNCAN"   (First Middle Last — last token is the last name)
  //   "SUSAN DUNCAN"         (First Last)
  //   "MADONNA"              (single token → first name only)
  // Middle is everything between the first and last token. This avoids the old
  // behaviour that lumped middle names into the last name (which the payer
  // matches on and would reject).
  private parseName(raw: any): { first: string; middle: string; last: string } {
    const out = { first: '', middle: '', last: '' };
    const text = (raw || '').toString().trim().replace(/\s+/g, ' ');
    if (!text) return out;

    if (text.includes(',')) {
      // "Last, First Middle"
      const [lastPart, ...rest] = text.split(',');
      out.last = (lastPart || '').trim();
      const firstParts = rest.join(',').trim().split(' ').filter(p => p);
      out.first  = firstParts[0] || '';
      out.middle = firstParts.slice(1).join(' ');
      return out;
    }

    const parts = text.split(' ').filter(p => p);
    if (parts.length === 1) {
      out.first = parts[0];
    } else if (parts.length === 2) {
      out.first = parts[0];
      out.last  = parts[1];
    } else {
      out.first  = parts[0];
      out.last   = parts[parts.length - 1];
      out.middle = parts.slice(1, -1).join(' ');
    }
    return out;
  }

  // Splits a concatenated US address into its parts. Handles the common shapes:
  //   "123 Main St, Louisville, KY 40212"      (street, city, state zip)
  //   "123 Main St, Louisville, KY, 40212"     (comma before zip)
  //   "123 Main St Louisville KY 40212"        (no commas — state+zip peeled off the end)
  // Anything that doesn't match a recognizable "ST 12345" tail stays in street,
  // so we never guess wrong parts into the N4 segment.
  private parseAddress(raw: any): { street: string; city: string; state: string; zip: string } {
    const out = { street: '', city: '', state: '', zip: '' };
    const text = (raw || '').toString().trim().replace(/\s+/g, ' ');
    if (!text) return out;

    // Peel "ST 12345(-6789)" (comma or space separated) off the end. The zip
    // part is lenient (4–9 digits) so malformed source data still splits into
    // the right fields — the zip control's validator will flag it there.
    const tail = text.match(/^(.*?)[,\s]+([A-Za-z]{2})[,\s]+(\d{5}-\d{4}|\d{4,9})$/);
    if (!tail) {
      out.street = text;
      return out;
    }
    out.state = tail[2].toUpperCase();
    out.zip   = tail[3];

    // Whatever precedes state/zip: last comma separates street from city.
    const rest = tail[1].replace(/,\s*$/, '');
    const lastComma = rest.lastIndexOf(',');
    if (lastComma > 0) {
      out.street = rest.substring(0, lastComma).trim();
      out.city   = rest.substring(lastComma + 1).trim();
    } else {
      out.street = rest.trim();
    }
    return out;
  }

  // Maps any gender/sex value (e.g. "MALE", "Female", "M", "NOT_SPECIFIED")
  // onto the intake select's M / F / U options.
  private normalizeGender(raw: any): string {
    const v = (raw || '').toString().trim().toUpperCase();
    if (!v) return '';
    if (v === 'F' || v.startsWith('FEMALE')) return 'F';
    if (v === 'M' || v.startsWith('MALE')) return 'M';
    return 'U';
  }

  // ─── Payer search (2,770 payers) ────────────────────────────────────────────
  // Type-ahead over a large list: normalized + token (multi-word) matching,
  // relevance ranking (id-exact → name-prefix → word-prefix → contains), match
  // highlighting. The picker shows EVERY match (2026-09-30, user decision — no
  // render cap): an empty search box lists all payers, people scroll.
  private payerSearchText = '';
  private payerTokens: string[] = [];      // normalized query tokens (drive highlighting)
  enrolledOnly = false;                    // "Enrollment required only" toggle
  payerMatchCount = 0;                     // number of payers listed

  // Precompute a normalized name/id on every payer once, so each keystroke
  // filters without re-normalizing 2,770 strings.
  private indexPayers(): void {
    for (const p of this.payers) {
      p._nname = this.normalizeSearch(p.name);
      p._nid   = this.normalizeSearch(p.id);
    }
  }

  // Lowercase, strip accents, turn punctuation into spaces, collapse spaces —
  // so "BC/BS", "bc bs" and "bcbs" all behave, and "(94123)" matches "94123".
  private normalizeSearch(s: any): string {
    return (s || '')
      .toString()
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  filterPayers(searchText: string) {
    this.payerSearchText = searchText || '';
    this.applyPayerFilter();
  }

  toggleEnrolledOnly() {
    this.enrolledOnly = !this.enrolledOnly;
    this.applyPayerFilter();
  }

  // True when any word within `text` (space-separated, normalized) starts with `token`.
  private wordStarts(text: string, token: string): boolean {
    return (' ' + text).includes(' ' + token);
  }

  private applyPayerFilter() {
    const q = this.normalizeSearch(this.payerSearchText);
    const tokens = q ? q.split(' ') : [];
    const qNoSpace = q.replace(/ /g, '');
    this.payerTokens = tokens;

    const pool = this.enrolledOnly
      ? this.payers.filter(p => p.enroll === 'Y')
      : this.payers;

    // Empty query — list every payer.
    if (!tokens.length) {
      this.setFilteredPayers(pool);
      return;
    }

    const scored: { p: any; tier: number }[] = [];
    for (const p of pool) {
      const name = p._nname || '';
      const id   = p._nid || '';
      const hay  = name + ' ' + id;
      // Require every token to appear somewhere (name or id).
      let all = true;
      for (const t of tokens) { if (!hay.includes(t)) { all = false; break; } }
      if (!all) continue;

      // Relevance tier (lower = better).
      let tier = 3;                                             // contains
      if (id === qNoSpace) tier = 0;                            // exact id
      else if (name.startsWith(q)) tier = 1;                    // name prefix
      else if (tokens.every(t => this.wordStarts(name, t) || this.wordStarts(id, t))) tier = 2; // word prefix
      scored.push({ p, tier });
    }

    scored.sort((a, b) =>
      a.tier - b.tier ||
      a.p.name.length - b.p.name.length ||
      a.p.name.localeCompare(b.p.name));

    this.setFilteredPayers(scored.map(s => s.p));
  }

  // The highlighted name is built ONCE per filter and kept on the payer: with every
  // payer on screen, a highlightPayer() call in the template would rebuild (and
  // re-set) the innerHTML of thousands of chips on each change detection.
  private setFilteredPayers(list: any[]) {
    for (const p of list) { p._hl = this.highlightPayer(p.name); }
    this.payerMatchCount = list.length;
    this.filteredPayers = list;
  }

  // Wraps the matched token(s) in <span class="ph-hl"> for the dropdown option.
  // Text is HTML-escaped first, then only our own spans are injected, so the
  // trusted static payer names render safely.
  highlightPayer(text: string): SafeHtml {
    const raw = (text || '').toString();
    const safe = this.escapeHtml(raw);
    if (!this.payerTokens.length) return this.sanitizer.bypassSecurityTrustHtml(safe);
    const pattern = this.payerTokens
      .map(t => this.escapeRegex(t))
      .filter(Boolean)
      .join('|');
    if (!pattern) return this.sanitizer.bypassSecurityTrustHtml(safe);
    const re = new RegExp('(' + pattern + ')', 'ig');
    return this.sanitizer.bypassSecurityTrustHtml(
      safe.replace(re, '<span class="ph-hl">$1</span>'));
  }

  private escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private escapeRegex(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  onPayerSelectOpened(opened: boolean) {
    if (opened) {
      this.applyPayerFilter();
      setTimeout(() => this.payerSearchInput?.nativeElement?.focus());
    } else {
      if (this.payerSearchInput?.nativeElement) this.payerSearchInput.nativeElement.value = '';
      this.payerSearchText = '';
      this.applyPayerFilter();
    }
  }

  getSelectedPayerName(): string {
    const id = this.intakeForm.get('payerId')?.value;
    if (!id) return '';
    const payer = this.payers.find(p => p.id === id);
    return payer ? `${payer.name} (${payer.id})` : id;
  }

  clearPayer(payerSelect?: { close: () => void }): void {
    this.intakeForm.get('payerId')?.setValue(null);
    this.intakeForm.get('payerId')?.markAsDirty();
    if (payerSelect && typeof payerSelect.close === 'function') {
      payerSelect.close();
    }
  }

  // ─── Full-screen payer picker ────────────────────────────────────────────────
  openPayerPicker(): void {
    this.payerSearchText = '';
    this.applyPayerFilter();
    this.showPayerPicker = true;
    // Focus the big search box once the overlay is in the DOM.
    setTimeout(() => this.payerPickerInput?.nativeElement?.focus(), 0);
  }

  closePayerPicker(): void {
    this.showPayerPicker = false;
  }

  selectPayerFromPicker(payer: any): void {
    this.intakeForm.get('payerId')?.setValue(payer.id);
    this.intakeForm.get('payerId')?.markAsDirty();
    this.closePayerPicker();
  }

  // Clears the selection but keeps the picker open so another can be chosen.
  clearPayerSelection(): void {
    this.intakeForm.get('payerId')?.setValue(null);
    this.intakeForm.get('payerId')?.markAsDirty();
  }

  closeDialog(): void {
    this.dialogRef.close({ acceptanceStatus: this.acceptanceStatus });
  }

  acceptVerification(): void {
    this.updateAcceptance(2);
  }

  rejectVerification(): void {
    this.updateAcceptance(3);
  }

  // Source of the dialog opener: 'referral' (default) | 'insuranceVerification' | 'core'
  private get caseSource(): string {
    return this.data?.source || 'referral';
  }

  // Build the {source, referralID?, insuranceVerificationID?, coreID?} envelope
  // that every unified Waystar endpoint expects. Includes whichever case ids
  // are present on the dialog data — the backend derives any missing ones.
  private buildCaseRef(): any {
    const rec = this.data?.selectedRecord || {};
    return {
      source:                  this.caseSource,
      referralID:              rec.referralID              ?? null,
      insuranceVerificationID: rec.insuranceVerificationID ?? null,
      coreID:                  rec.coreID                  ?? null,
      organizationID:          this.data?.organizationID,
      officeID:                this.data?.officeID,
      userID:                  this.data?.userID
    };
  }

  // At least one id must be present for the backend to identify the case.
  private hasCaseRef(): boolean {
    const c = this.buildCaseRef();
    return !!(c.referralID || c.insuranceVerificationID || c.coreID);
  }

  private updateAcceptance(status: number): void {
    if (!this.hasCaseRef()) return;
    this.isUpdatingAcceptance = true;
    const req = { ...this.buildCaseRef(), acceptanceStatus: status };
    this.formsService.updateWaystarAcceptanceV2(req).subscribe({
      next: (res) => {
        this.isUpdatingAcceptance = false;
        if (res?.data) {
          this.acceptanceStatus = status;
          const rec = this.data?.selectedRecord;
          if (rec) rec.waystarAcceptanceStatus = status;
          const label = status === 2 ? 'Accepted' : 'Rejected';
          this.toastr.success(`Verification ${label}.`, label);
        } else {
          this.toastr.error('Failed to update status.', 'Error');
        }
      },
      error: () => {
        this.isUpdatingAcceptance = false;
        this.toastr.error('Failed to update status.', 'Error');
      }
    });
  }

  getAcceptanceBadgeClass(): string {
    if (this.acceptanceStatus === 2) return 'badge-accepted';
    if (this.acceptanceStatus === 3) return 'badge-rejected';
    if (this.acceptanceStatus === 1) return 'badge-pending';
    return '';
  }

  getAcceptanceLabel(): string {
    if (this.acceptanceStatus === 2) return 'Accepted';
    if (this.acceptanceStatus === 3) return 'Rejected';
    if (this.acceptanceStatus === 1) return 'Pending Decision';
    return '';
  }

  selectSection(section: string) {
    this.activeSection = section;
    if (section === 'Verification History') this.loadHistory();
    if (section === 'Notes & Audit Trail')  { this.loadNotes(); if (!this.historyRecords.length) this.loadHistory(); }
  }

  loadHistory() {
    if (!this.hasCaseRef()) return;
    this.isLoadingHistory = true;
    this.selectedHistoryRecord = null;
    this.formsService.getWaystarVerificationHistoryV2(this.buildCaseRef()).subscribe({
      next: (res) => {
        const records = res?.data?.records || [];
        this.historyRecords = records.map(this.mapHistoryRecord);
        this.isLoadingHistory = false;
        if (this.historyRecords.length > 0) {
          this.selectHistoryRecord(this.historyRecords[0]);
        }
      },
      error: () => {
        this.toastr.error('Could not load verification history.', 'Error');
        this.isLoadingHistory = false;
      }
    });
  }

  // The new endpoint returns PascalCase fields (WaystarVerificationID, IntakeJson, …)
  // while the existing template expects historyID / intakeJson / responseJson / verifiedOn / fileStatus.
  // Adapt here so the UI stays unchanged.
  private mapHistoryRecord = (r: any) => ({
    historyID:    r.waystarVerificationID ?? r.WaystarVerificationID,
    intakeJson:   r.intakeJson   ?? r.IntakeJson,
    responseJson: r.responseJson ?? r.ResponseJson,
    verifiedOn:   r.verifiedOn   ?? r.VerifiedOn,
    fileStatus:   r.fileStatus   ?? r.FileStatus,
    carrierName:  r.carrierName  ?? r.CarrierName,
    inquiryId:    r.inquiryId    ?? r.InquiryId,
    source:       r.source       ?? r.Source
  });

  selectHistoryRecord(record: any) {
    if (this.selectedHistoryRecord?.historyID === record.historyID) {
      this.selectedHistoryRecord = null;
      return;
    }
    this.selectedHistoryRecord = record;
    try { this.selectedHistoryRecord._intake   = JSON.parse(record.intakeJson); } catch { this.selectedHistoryRecord._intake = null; }
    try { this.selectedHistoryRecord._response = JSON.parse(record.responseJson); } catch { this.selectedHistoryRecord._response = null; }
  }

  loadHistoryIntoViewer(record: any) {
    try { this.waystarResponse = JSON.parse(record.responseJson); } catch { return; }
    this.updateFailureState(this.waystarResponse);
    try { this.patchIntakeNonEmpty(JSON.parse(record.intakeJson)); } catch { }
    this.activeSection = 'Electronic Verification';
    this.activeEvTab   = 'summary';
    this.toastr.info(`Loaded verification from ${this.formatHistoryDate(record.verifiedOn)}`, 'History');
  }

  formatHistoryDate(dateStr: string): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleString();
  }

  getHistoryStatusClass(status: string): string {
    return (status || '').toUpperCase() === 'ACTIVE' || status === '1' ? 'badge-active' : 'badge-inactive';
  }

  // ─── Notes ──────────────────────────────────────────────────────────────────

  loadNotes() {
    if (!this.hasCaseRef()) return;
    this.isLoadingNotes = true;
    this.formsService.getWaystarNotesV2(this.buildCaseRef()).subscribe({
      next:  (res) => {
        const list = res?.data?.notes || [];
        this.notes = list.map((n: any) => ({
          noteID:        n.noteID        ?? n.NoteID,
          noteText:      n.noteText      ?? n.NoteText,
          createdOn:     n.createdOn     ?? n.CreatedOn,
          createdByName: n.createdByName ?? n.CreatedByName
        }));
        this.isLoadingNotes = false;
      },
      error: () => { this.toastr.error('Could not load notes.', 'Error'); this.isLoadingNotes = false; }
    });
  }

  submitNote() {
    if (!this.newNoteText.trim()) { this.toastr.warning('Note cannot be empty.', 'Warning'); return; }
    if (!this.hasCaseRef()) return;
    this.isSubmittingNote = true;
    const req = { ...this.buildCaseRef(), noteText: this.newNoteText.trim() };
    this.formsService.addWaystarNoteV2(req).subscribe({
      next: (res) => {
        this.isSubmittingNote = false;
        if (res?.data) {
          this.newNoteText = '';
          this.toastr.success('Note added.', 'Saved');
          this.loadNotes();
        } else {
          this.toastr.error('Failed to save note.', 'Error');
        }
      },
      error: () => { this.isSubmittingNote = false; this.toastr.error('Failed to save note.', 'Error'); }
    });
  }

  // ─── Coverage Detection ──────────────────────────────────────────────────────

  buildCoverageDetectionRequest(): any {
    const rec = this.data?.selectedRecord;
    const form = this.intakeForm.value;
    let dob = this.toYyyyMmDd(form.dateOfBirth);
    if (!dob && rec?.age) {
      const year = new Date().getFullYear() - parseInt(rec.age, 10);
      dob = `${year}0101`;
    }
    const today = new Date();
    const svcDate = `${today.getFullYear()}${String(today.getMonth()+1).padStart(2,'0')}${String(today.getDate()).padStart(2,'0')}`;
    return {
      firstName:      form.subscriberFirstName || rec?.name?.split(' ')[0] || '',
      middleName:     form.subscriberMiddleName || '',
      lastName:       form.subscriberLastName  || rec?.name?.split(' ').slice(1).join(' ') || '',
      dateOfBirth:    dob,
      providerNpi:    form.providerNpi  || '',
      providerName:   form.providerName || rec?.locationName || '',
      providerTaxId:  form.providerTaxId || '',
      serviceFromDate: svcDate,
      gender:         form.gender || '',
      ssn:            this.cleanSsn(form.ssn),
      address:        form.address || '',
      city:           form.city || '',
      state:          (form.state || '').toUpperCase(),
      zip:            form.zip || '',
      medicalRecordNumber: (form.medicalRecordNumber || String(rec?.mrn || rec?.medicalRecordNumber || '')).replace(/\s/g, ''),
      responseType:   'FullJSON',
      // REF*EJ patient-matching identifier. Referral ID when present; entry
      // paths without one (e.g. Core) fall back to their own case ID so
      // Coverage Discovery is never blocked for lack of an account number.
      accountNumber:  String(rec?.referralID || rec?.coreID || rec?.insuranceVerificationID || '')
    };
  }

  // Entry point from the secondary panel button. If a payer is already
  // selected, surface the cost-confirm modal so the user makes a deliberate
  // choice between the $0.25 standard check and the $4.50 discovery call.
  attemptDetectCoverage(): void {
    const payerSelected = !!this.intakeForm.get('payerId')?.value;
    if (payerSelected) {
      this.coverageConfirmReason = '';
      this.showCoverageCostConfirm = true;
      return;
    }
    this.detectCoverage();
  }

  cancelCoverageConfirm(): void {
    this.showCoverageCostConfirm = false;
    this.coverageConfirmReason = '';
  }

  switchToWaystarFromConfirm(): void {
    this.showCoverageCostConfirm = false;
    this.coverageConfirmReason = '';
    this.runWaystarCheck();
  }

  confirmAndRunCoverage(): void {
    if (!this.coverageConfirmReason) return;
    this.showCoverageCostConfirm = false;
    this.detectCoverage();
  }

  detectCoverage(): void {
    const payload: any = this.buildCoverageDetectionRequest();
    // Mirrors the API-side ValidateRequest (Waystar companion guide required set).
    const missing: string[] = [];
    if (!payload.firstName)     missing.push('First Name');
    if (!payload.lastName)      missing.push('Last Name');
    if (!payload.dateOfBirth)   missing.push('Date of Birth');
    if (!payload.providerName)  missing.push('Provider Name');
    if (!payload.providerNpi || !/^\d{10}$/.test(payload.providerNpi)) missing.push('Provider NPI (10 digits)');
    if (!payload.accountNumber) missing.push('Account Number (referral ID)');
    if (missing.length) {
      this.toastr.warning(`Required for Coverage Discovery: ${missing.join(', ')}.`, 'Missing Data');
      return;
    }
    if (this.coverageConfirmReason) {
      payload.discoveryReason = this.coverageConfirmReason;
    }
    this.isDetectingCoverage = true;
    this.coverageDetectionResults = null;
    this.formsService.detectCoverageRealtime(payload).subscribe({
      next: (res) => {
        this.isDetectingCoverage = false;
        this.coverageDetectionResults = res;
        this.saveCoverageDetectionResults(res);
        this.activeSection = 'Coverage Detection';
        if (!res?.coverageFound) {
          this.toastr.info('No coverage found. Try adding SSN or address.', 'No Coverage');
        } else {
          this.toastr.success(`Found ${res.coverages?.length || 0} coverage(s).`, 'Coverage Detected');
        }
      },
      error: () => {
        this.isDetectingCoverage = false;
        this.toastr.error('Coverage detection failed. Please try again.', 'Error');
      }
    });
  }

  private saveCoverageDetectionResults(res: any): void {
    if (!this.hasCaseRef()) return;
    const req = { ...this.buildCaseRef(), coverageDetectionJson: JSON.stringify(res) };
    this.formsService.saveWaystarCoverageDetectionV2(req).subscribe({
      error: () => this.toastr.warning('Coverage results displayed but failed to save.', 'Save Error')
    });
  }

  useDetectedCoverage(coverage: any): void {
    const payerId = coverage?.payerId;
    const memberId = coverage?.memberId;
    if (payerId && this.payers.length) {
      const found = this.payers.find(p => p.id === payerId);
      if (found) {
        this.intakeForm.patchValue({ payerId: found.id });
      }
    }
    if (memberId) {
      this.intakeForm.patchValue({ subscriberId: memberId });
    }
    this.activeSection = 'Electronic Verification';
    this.runWaystarCheck();
  }

  getMatchScoreClass(score: string): string {
    const s = (score || '').toLowerCase();
    if (s.includes('strong'))   return 'match-strong';
    if (s.includes('probable')) return 'match-probable';
    if (s.includes('weak'))     return 'match-weak';
    if (s.includes('nomatch') || s.includes('no match')) return 'match-none';
    return 'match-unknown';
  }

  getMatchScoreLabel(score: string): string {
    const s = (score || '').toLowerCase();
    if (s.includes('strong'))   return 'Strong Match';
    if (s.includes('probable')) return 'Probable Match';
    if (s.includes('weak'))     return 'Weak Match';
    if (s.includes('nomatch') || s.includes('no match')) return 'No Match';
    return score || 'Unknown';
  }

  formatCoverageDate(dateStr: string): string {
    if (!dateStr) return '—';
    if (dateStr.length === 8) {
      return `${dateStr.substring(4,6)}/${dateStr.substring(6,8)}/${dateStr.substring(0,4)}`;
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
  }

  getHistoryPlanName(po: any): string {
    if (!po) return '—';
    return po.PlanName || (po.Plans && po.Plans[0] && po.Plans[0].InsurancePlanName) || '—';
  }

  getHistoryBenefit30(po: any): any {
    if (!po || !po.Benefits) return null;
    return po.Benefits['30'] || null;
  }

  // ─── Waystar API ────────────────────────────────────────────────────────────

  // Labels for the intake controls, used to build readable validation messages.
  private readonly INTAKE_FIELD_LABELS: { [key: string]: string } = {
    payerId:             'Payer',
    providerName:        'Provider Name',
    providerNpi:         'Provider NPI (10 digits)',
    providerTaxId:       'Provider Tax ID (9-digit EIN)',
    subscriberId:        'Subscriber / Member ID',
    subscriberFirstName: 'First Name',
    subscriberLastName:  'Last Name',
    dateOfBirth:         'Date of Birth',
    state:               'State (2-letter code)',
    zip:                 'ZIP Code'
  };

  // Controls that feed the 270 eligibility request. Address fields are not on
  // this list on purpose: they're only sent for Coverage Discovery, so a
  // malformed optional zip/state must not block a standard eligibility check.
  private readonly ELIGIBILITY_CONTROLS = [
    'payerId', 'providerName', 'providerNpi', 'providerTaxId',
    'subscriberId', 'subscriberFirstName', 'subscriberLastName', 'dateOfBirth'
  ];

  private getInvalidFieldLabels(controls: string[]): string[] {
    return controls
      .filter(key => this.intakeForm.get(key)?.invalid)
      .map(key => this.INTAKE_FIELD_LABELS[key] || key);
  }

  // ── Address group (all-or-nothing) ─────────────────────────────────────────
  // Street / City / State / ZIP must be either all filled or all empty. A
  // partially-filled address hurts the Waystar match, so a run is blocked until
  // it's completed or cleared.
  private readonly ADDRESS_CONTROLS = ['address', 'city', 'state', 'zip'];
  private readonly ADDRESS_LABELS: { [k: string]: string } = {
    address: 'Street Address', city: 'City', state: 'State', zip: 'ZIP'
  };

  private addressFilledKeys(): string[] {
    return this.ADDRESS_CONTROLS.filter(k => !!(this.intakeForm.get(k)?.value || '').toString().trim());
  }

  // True when some — but not all — address fields are filled.
  isAddressPartial(): boolean {
    const filled = this.addressFilledKeys().length;
    return filled > 0 && filled < this.ADDRESS_CONTROLS.length;
  }

  // For the template: highlight an address field that must be filled because
  // another address field was entered.
  addressFieldMissing(control: string): boolean {
    const v = (this.intakeForm.get(control)?.value || '').toString().trim();
    return !v && this.addressFilledKeys().length > 0;
  }

  // Address fields blocking a run: empty ones (when the group is partial) plus
  // any provided State/ZIP with an invalid format.
  private invalidAddressFields(): { key: string; label: string }[] {
    const filled = this.addressFilledKeys();
    const out: { key: string; label: string }[] = [];
    if (filled.length === 0) return out; // all empty → address not used, fine

    // Partial → every empty field is now required.
    this.ADDRESS_CONTROLS
      .filter(k => !filled.includes(k))
      .forEach(k => out.push({ key: k, label: this.ADDRESS_LABELS[k] }));

    // Provided State/ZIP must still be well-formed.
    ['state', 'zip'].forEach(k => {
      const v = (this.intakeForm.get(k)?.value || '').toString().trim();
      if (v && this.intakeForm.get(k)?.hasError('pattern')) {
        out.push({ key: k, label: this.ADDRESS_LABELS[k] + ' format' });
      }
    });
    return out;
  }

  // Converts a datepicker Date (or date string) to the yyyyMMdd format the API expects.
  private toYyyyMmDd(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  }

  // Maps the intake form to the API EligibilityRequest model. The form's
  // dateOfBirth is a Date object and must be sent as subscriberDob (yyyyMMdd);
  // posting intakeForm.value raw would leave subscriberDob empty on the API side.
  private buildEligibilityPayload(): any {
    const form = this.intakeForm.value;
    return {
      payerId:             form.payerId,
      // Provider on File — always sent with the check (office-level default).
      // providerIdMode controls which of NPI / Tax ID the API actually emits.
      providerName:        (form.providerName || '').trim(),
      providerNpi:         (form.providerNpi || '').trim(),
      providerTaxId:       (form.providerTaxId || '').trim(),
      providerIdMode:      form.providerIdMode || 'npi',
      subscriberId:         (form.subscriberId || '').trim(),
      subscriberFirstName:  (form.subscriberFirstName || '').trim(),
      subscriberMiddleName: (form.subscriberMiddleName || '').trim(),
      subscriberLastName:   (form.subscriberLastName || '').trim(),
      subscriberDob:        this.toYyyyMmDd(form.dateOfBirth),
      // Optional match-improving details — only sent when the user filled them.
      subscriberGender:     (form.gender || '').trim(),
      subscriberSsn:        this.cleanSsn(form.ssn),
      subscriberAddress:    (form.address || '').trim(),
      subscriberCity:       (form.city || '').trim(),
      subscriberState:      (form.state || '').trim().toUpperCase(),
      subscriberZip:        (form.zip || '').trim(),
      medicalRecordNumber:  (form.medicalRecordNumber || '').trim(),
      responseType:         'FullJSON'
    };
  }

  runWaystarCheck() {
    const mode = this.intakeForm.get('providerIdMode')?.value || 'npi';
    // NPI is only validated when the chosen mode actually sends it.
    const controls = this.ELIGIBILITY_CONTROLS.filter(c => c !== 'providerNpi' || mode !== 'name_tax');
    const invalid = this.getInvalidFieldLabels(controls);
    if (invalid.length) {
      this.toastr.warning(`Please correct: ${invalid.join(', ')}.`, 'Missing Data');
      controls.forEach(key => this.intakeForm.get(key)?.markAsTouched());
      return;
    }
    // Tax-ID modes require a 9-digit Tax ID on the office Provider on File.
    if (mode === 'npi_tax' || mode === 'name_tax') {
      const tax = (this.intakeForm.get('providerTaxId')?.value || '').toString().trim();
      if (!/^\d{9}$/.test(tax)) {
        this.toastr.warning('A 9-digit Provider Tax ID is required for the selected provider option — add it to Provider on File.', 'Missing Tax ID');
        this.intakeForm.get('providerTaxId')?.markAsTouched();
        return;
      }
    }
    // Address is all-or-nothing — a partial address is blocked. Open the optional
    // panel and flag the offending fields so the user can complete or clear it.
    const addrBad = this.invalidAddressFields();
    if (addrBad.length) {
      this.showOptionalDetails = true;
      addrBad.forEach(f => this.intakeForm.get(f.key)?.markAsTouched());
      this.toastr.warning(
        `Complete the full address or leave it empty. Check: ${addrBad.map(f => f.label).join(', ')}.`,
        'Address incomplete');
      return;
    }
    this.isLoading = true;
    this.waystarFailure = null;
    const payload = this.buildEligibilityPayload();
    this.formsService.verifyEligibility(payload).subscribe({
      next:  (res) => this.handleWaystarResponse(res),
      error: (err) => {
        this.isLoading = false;
        // API errors carry a WaystarErrorResponse body; its message can be raw
        // HTML from the Waystar gateway — strip tags and truncate for the toast.
        const raw = (err?.error?.message || err?.error?.Message || '').toString();
        const msg = raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 300);
        if (err?.status === 400) {
          this.toastr.warning(msg || 'The request was rejected — please review the form fields.', 'Validation Error');
          this.intakeForm.markAllAsTouched();
        } else if (err?.status >= 500) {
          this.toastr.error(msg || 'The Waystar service is unavailable right now. Please try again shortly.', 'Server Error');
        } else {
          this.toastr.error(msg || 'Waystar check failed. Please check your connection and try again.', 'Error');
        }
      }
    });
  }

  // Payer-level failure surfaced by a 200 response (FileStatus 3 / FailReason /
  // FailCode / 997 alerts). Drives the red banner in Electronic Verification.
  waystarFailure: { code: string; reason: string; hint: string; alerts: string[] } | null = null;

  // A 200 from the API does not mean the payer accepted the inquiry — reject
  // reasons come back inside ParsedOutput. Derive the failure state so the UI
  // can present it instead of implying success.
  private updateFailureState(response: any): void {
    const parsed = response?.ParsedOutput || {};
    const failReason = (parsed.FailReason || '').toString();
    const failCode   = (parsed.FailCode || '').toString();
    const failed = !!failReason || !!failCode || parsed.FileStatus === 3;

    if (!failed) { this.waystarFailure = null; return; }

    const reason = failReason === '997'
      ? 'The request was rejected by format validation (997) before reaching the payer.'
      : failReason || 'The payer returned an error for this inquiry.';

    this.waystarFailure = {
      code:   failCode,
      reason: reason,
      hint:   this.failureHint(failCode),
      alerts: response?.WaystarAlerts || []
    };
  }

  // Actionable next step per AAA reject code, so the user isn't stuck.
  private failureHint(code: string): string {
    switch ((code || '').trim()) {
      case '15': return 'Required information is missing — review the intake fields and re-run.';
      case '42': return 'The payer could not respond right now — try again in a few minutes.';
      case '43': return 'Check that the Provider NPI is correct and enrolled with this payer.';
      case '44': return 'The provider name is not registered with this carrier — verify the provider name matches the Waystar enrollment exactly, or contact Waystar to register it.';
      case '58':
      case '71': return 'Check the patient\'s date of birth against the insurance card.';
      case '72': return 'Check the Subscriber / Member ID against the insurance card.';
      case '73': return 'Check the patient\'s name spelling against the insurance card.';
      case '75': return 'Subscriber not found — verify Member ID, name and DOB, or try Coverage Discovery.';
      case '79': return 'Connectivity issue with the payer — try again shortly.';
      default:   return 'Correct the highlighted information and re-run, or contact support with the Inquiry ID.';
    }
  }

  handleWaystarResponse(response: any) {
    this.waystarResponse = response;
    this.isLoading = false;
    this.activeEvTab = 'summary';
    this.activeSection = 'Electronic Verification';
    this.updateFailureState(response);
    if (this.waystarFailure) {
      this.toastr.warning(this.waystarFailure.reason, 'Verification Failed');
    } else {
      this.toastr.success('Verification complete.', 'Waystar');
    }
    this.persistVerification();
  }

  persistVerification() {
    if (!this.hasCaseRef() || !this.waystarResponse) return;

    const parsed = this.waystarResponse?.ParsedOutput || {};
    const reqModel = {
      ...this.buildCaseRef(),
      inquiryId:    this.waystarResponse?.InquiryId || null,
      intakeJson:   JSON.stringify(this.intakeForm.value),
      responseJson: JSON.stringify(this.waystarResponse),
      carrierName:  parsed.CarrierName || '',
      fileStatus:   parsed.FileStatusDescription || ''
    };

    this.formsService.saveWaystarVerificationV2(reqModel).subscribe({
      next: (res) => {
        const rec = this.data?.selectedRecord;
        if (rec) {
          rec.waystarIntakeJson   = reqModel.intakeJson;
          rec.waystarResponseJson = reqModel.responseJson;
          rec.waystarInquiryId    = reqModel.inquiryId;
          rec.waystarCarrierName  = reqModel.carrierName;
          rec.waystarFileStatus   = reqModel.fileStatus;
          rec.waystarVerifiedOn   = new Date().toISOString();
          if (this.acceptanceStatus === null) {
            this.acceptanceStatus = 1;
            rec.waystarAcceptanceStatus = 1;
          }
        }
        // Backend echoes back any FKs it derived (e.g. InsuranceVerificationID
        // when called from Core) — keep them on data.selectedRecord so future
        // calls in this dialog session don't re-resolve.
        const echoed = res?.data || {};
        const derivedRec = this.data?.selectedRecord;
        if (derivedRec) {
          if (echoed.insuranceVerificationID && !derivedRec.insuranceVerificationID) {
            derivedRec.insuranceVerificationID = echoed.insuranceVerificationID;
          }
          if (echoed.referralID && !derivedRec.referralID) {
            derivedRec.referralID = echoed.referralID;
          }
          if (echoed.coreID && !derivedRec.coreID) {
            derivedRec.coreID = echoed.coreID;
          }
        }
        this.toastr.success('Verification saved.', 'Saved');
      },
      error: () => this.toastr.warning('Verification displayed but failed to save.', 'Save Error')
    });
  }

  copyJson() {
    navigator.clipboard.writeText(this.getFormattedJson()).then(() => {
      this.toastr.success('JSON copied to clipboard.', 'Copied');
    });
  }

  // ─── Parsed output helpers ──────────────────────────────────────────────────

  get parsedOutput(): any { return this.waystarResponse?.ParsedOutput || null; }
  get subscriber(): any   { return this.parsedOutput?.Subscriber || null; }
  get plans(): any[]      { return this.parsedOutput?.Plans || []; }

  getServiceTypeName(code: string): string {
    if (!code) return '-';
    const first = code.split('^')[0];
    return SERVICE_TYPE_NAMES[first] || code;
  }

  getStatusBadgeClass(status: string): string {
    return (status || '').toUpperCase() === 'ACTIVE' ? 'badge-active' : 'badge-inactive';
  }

  getPrimaryPlan(): any {
    return (
      this.plans.find(p => p.InsurancePlan === '30' || p.InsurancePlan?.startsWith('30^')) ||
      this.plans[0] ||
      null
    );
  }

  getBenefit(planType: string): any {
    return this.parsedOutput?.Benefits?.[planType] || null;
  }

  getBenefitsList(): { planType: string; name: string; ind: any; fam: any }[] {
    if (!this.parsedOutput?.Benefits) return [];
    return Object.keys(this.parsedOutput.Benefits)
      .filter(k => k !== 'It-none')
      .map(k => ({
        planType: k,
        name:     this.getServiceTypeName(k),
        ind:      this.parsedOutput.Benefits[k]?.IND || null,
        fam:      this.parsedOutput.Benefits[k]?.FAM || null
      }));
  }

  getFinancialSummary() {
    const b30  = this.getBenefit('30');
    const b12  = this.getBenefit('12');
    const plan = this.getPrimaryPlan();
    return {
      indDeductible:    this.fmtCurrency(b30?.IND?.BinDed    ?? plan?.PlanBinDed),
      indDeductibleRem: this.fmtCurrency(b30?.IND?.BinDedRem ?? plan?.PlanBinDedRem),
      famDeductible:    this.fmtCurrency(b30?.FAM?.BinDed    ?? plan?.PlanBonDed),
      famDeductibleRem: this.fmtCurrency(b30?.FAM?.BinDedRem ?? plan?.PlanBonDedRem),
      indOop:           this.fmtCurrency(b30?.IND?.BinOop),
      indOopRem:        this.fmtCurrency(b30?.IND?.BinOopRem ?? plan?.PlanBinOopRem),
      famOop:           this.fmtCurrency(b30?.FAM?.BinOop),
      famOopRem:        this.fmtCurrency(b30?.FAM?.BinOopRem),
      coInsIn:          this.fmtCoIns(plan?.CoInsuranceInNetwork,  b12?.IND?.BinCovgPerc),
      coInsOut:         this.fmtCoIns(plan?.CoInsuranceOutNetwork, b12?.IND?.BonCovgPerc),
      copay:            b12?.IND?.BinCopay1 !== undefined ? this.fmtCurrency(b12.IND.BinCopay1) : '-'
    };
  }

  getOtherInsurance(): any[] {
    return this.parsedOutput?.OtherInsurance || [];
  }

  getMedicaidProviders(): any[] {
    return this.parsedOutput?.MedicaidProvidersOther || [];
  }

  getEffectiveDate(): string {
    const dtps = this.parsedOutput?.Dtps;
    return dtps && dtps[0] ? dtps[0].Date : '';
  }

  getCoveragePercent(ind: any): string {
    if (!ind) return '-';
    if (ind.BinCovgPerc) return ind.BinCovgPerc;
    if (ind.CovgPercs && ind.CovgPercs[0]) return ind.CovgPercs[0].Value || '-';
    return '-';
  }

  getPlanNoteMsg(ind: any): string {
    if (!ind || !ind.PlanNotes || !ind.PlanNotes[0]) return '';
    return ind.PlanNotes[0].Msg || '';
  }

  // ─── Recursive parsed-output renderer helpers ──────────────────────────────

  getValueType(val: any): 'primitive' | 'array' | 'object' | 'empty' {
    if (val === null || val === undefined || val === '') return 'empty';
    if (Array.isArray(val)) return val.length === 0 ? 'empty' : 'array';
    if (typeof val === 'object') return Object.keys(val).length === 0 ? 'empty' : 'object';
    return 'primitive';
  }

  objectEntries(obj: any): { key: string; value: any }[] {
    if (!obj || typeof obj !== 'object') return [];
    return Object.keys(obj).map(k => ({ key: k, value: obj[k] }));
  }

  formatKeyLabel(key: string): string {
    if (!key) return '';
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, c => c.toUpperCase())
      .replace(/\s+/g, ' ')
      .trim();
  }

  formatApiDate(dateStr: string): string {
    if (!dateStr) return '-';
    if (dateStr.length === 17 && dateStr.charAt(8) === '-') {
      const a = dateStr.substring(0, 8);
      const b = dateStr.substring(9, 17);
      return `${this.formatApiDate(a)} – ${this.formatApiDate(b)}`;
    }
    if (dateStr.includes('/')) return dateStr;
    if (dateStr.includes('-')) return dateStr;
    if (dateStr.length === 8) {
      return `${dateStr.substring(4, 6)}/${dateStr.substring(6, 8)}/${dateStr.substring(0, 4)}`;
    }
    return dateStr;
  }

  // ─── Friendly Parsed Output helpers ────────────────────────────────────────

  benefitSearch: string = '';
  expandedBenefits: Set<string> = new Set<string>();

  onBenefitSearch(event: any) {
    this.benefitSearch = (event?.target?.value || '').toString();
  }

  toggleBenefit(code: string) {
    if (this.expandedBenefits.has(code)) this.expandedBenefits.delete(code);
    else this.expandedBenefits.add(code);
  }

  isBenefitExpanded(code: string): boolean {
    return this.expandedBenefits.has(code);
  }

  expandAllBenefits() {
    this.getBenefitsList().forEach(b => this.expandedBenefits.add(b.planType));
  }

  collapseAllBenefits() {
    this.expandedBenefits.clear();
  }

  filteredBenefits(): { planType: string; name: string; ind: any; fam: any }[] {
    const list = this.getBenefitsList();
    if (!this.benefitSearch) return list;
    const q = this.benefitSearch.toLowerCase();
    return list.filter(b =>
      b.planType.toLowerCase().includes(q) ||
      b.name.toLowerCase().includes(q) ||
      ((b.ind?.InsuranceType || '') + '').toLowerCase().includes(q)
    );
  }

  getBenefitSummary(b: any): string {
    const ind = b?.ind;
    if (!ind) return '';
    const parts: string[] = [];
    if (ind.BinCovgPerc)                                   parts.push(ind.BinCovgPerc + ' covered');
    if (ind.BinDed !== undefined && ind.BinDed !== null)   parts.push('Ded ' + this.formatMoney(ind.BinDed));
    if (ind.BinCopay1 !== undefined && ind.BinCopay1 !== null) parts.push('Copay ' + this.formatMoney(ind.BinCopay1));
    if (ind.BinOop !== undefined && ind.BinOop !== null)   parts.push('OOP ' + this.formatMoney(ind.BinOop));
    return parts.slice(0, 3).join(' · ');
  }

  formatMoney(val: any): string {
    if (val === null || val === undefined || val === '') return '—';
    const n = parseFloat(val);
    if (isNaN(n)) return String(val);
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  formatPercentish(val: any): string {
    if (val === null || val === undefined || val === '') return '—';
    const s = String(val);
    if (s.endsWith('%')) return s;
    const n = parseFloat(s);
    if (!isNaN(n)) {
      if (n <= 1) return Math.round(n * 100) + '%';
      return Math.round(n) + '%';
    }
    return s;
  }

  formatNetFlag(flag: string): string {
    switch ((flag || '').toUpperCase()) {
      case 'Y': return 'In-Network';
      case 'N': return 'Out-of-Network';
      case 'W': return 'Both';
      case '':  return '—';
      default:  return flag;
    }
  }

  isPatientSameAsSubscriber(): boolean {
    const s = this.subscriber, p = this.parsedOutput?.Patient;
    if (!s || !p) return false;
    return s.First === p.First && s.Last === p.Last && s.Dob === p.Dob;
  }

  private PLAN_LABEL_OVERRIDES: { [k: string]: string } = {
    'InsurancePlan':                 'Service Type Code(s)',
    'InsurancePlanName':             'Insurance Plan Name',
    'ServiceType':                   'Service Type Code',
    'Name':                          'Plan Code',
    'ActiveDate':                    'Active Date',
    'IsActive':                      'Is Active',
    'FileStatus':                    'File Status',
    'FileStatusDescription':         'Status',
    'PatientEffectiveBenefitDate':   'Patient Effective Date',
    'DeductibleInNetwork':           'Deductible (In-Network)',
    'DeductibleOutNetwork':          'Deductible (Out-of-Network)',
    'CoInsuranceInNetwork':          'Co-Insurance (In-Network)',
    'CoInsuranceOutNetwork':         'Co-Insurance (Out-of-Network)',
    'PlanBinDed':                    'In-Network Plan Deductible',
    'PlanBinDedRem':                 'In-Network Deductible Remaining',
    'PlanBinOopRem':                 'In-Network OOP Remaining',
    'PlanBonDed':                    'Out-of-Network Plan Deductible',
    'PlanBonDedRem':                 'Out-of-Network Deductible Remaining',
    'HospitalInpatientBinCovgPerc':  'Hospital Inpatient Coverage',
    'ErBinCovgPerc':                 'Emergency Room Coverage',
    'Oi1Name':                       'Other Insurance #1 — Name',
    'Oi1Phone':                      'Other Insurance #1 — Phone',
    'Oi1PlanType':                   'Other Insurance #1 — Plan Type'
  };

  private MONEY_KEYS = new Set([
    'DeductibleInNetwork', 'DeductibleOutNetwork',
    'PlanBinDed', 'PlanBinDedRem', 'PlanBinOopRem',
    'PlanBonDed', 'PlanBonDedRem', 'PlanBinOop', 'PlanBonOop'
  ]);

  private PERCENT_KEYS = new Set(['CoInsuranceInNetwork', 'CoInsuranceOutNetwork']);

  getPlanLabel(key: string): string {
    return this.PLAN_LABEL_OVERRIDES[key] || this.formatKeyLabel(key);
  }

  formatPlanField(key: string, value: any): string {
    if (this.MONEY_KEYS.has(key))   return this.formatMoney(value);
    if (this.PERCENT_KEYS.has(key)) {
      const n = parseFloat(value);
      if (!isNaN(n) && n <= 1) return this.formatPercentish(value) + ' patient';
      return this.formatPercentish(value);
    }
    if (key === 'IsActive')   return value ? 'Yes' : 'No';
    if (key === 'FileStatus') return value === 1 || value === '1' ? 'Active (1)' : String(value);
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  getPlanEntries(plan: any): { key: string; label: string; value: string; isHighlight: boolean }[] {
    if (!plan) return [];
    const skip = new Set(['InsurancePlanName', 'FileStatusDescription']);
    const highlight = new Set(['PlanBinDedRem', 'PlanBinOopRem', 'DeductibleInNetwork', 'DeductibleOutNetwork']);
    return Object.keys(plan)
      .filter(k => !skip.has(k))
      .map(k => ({
        key:         k,
        label:       this.getPlanLabel(k),
        value:       this.formatPlanField(k, plan[k]),
        isHighlight: highlight.has(k)
      }));
  }

  private KNOWN_BENEFIT_KEYS = new Set([
    'CovgPercs', 'Copays', 'Deds', 'DedRems', 'Oops', 'OopRems',
    'Days', 'Dtps', 'Descriptions', 'PlanNotes',
    'CoverageStatus', 'PlanType', 'InsuranceType',
    'BinCovgPerc', 'BonCovgPerc',
    'BinDed', 'BinDedRem', 'BonDed', 'BonDedRem',
    'BinOop', 'BinOopRem', 'BonOop', 'BonOopRem',
    'BinCopay1', 'BinNotes', 'BinDaysMsg'
  ]);

  getBenefitOtherFields(d: any): { key: string; label: string; value: any }[] {
    if (!d) return [];
    return Object.keys(d)
      .filter(k => !this.KNOWN_BENEFIT_KEYS.has(k))
      .map(k => ({ key: k, label: this.formatKeyLabel(k), value: d[k] }));
  }

  hasNetworkSummary(d: any): boolean {
    if (!d) return false;
    return d.BinCovgPerc !== undefined || d.BonCovgPerc !== undefined ||
           d.BinDed !== undefined || d.BonDed !== undefined ||
           d.BinDedRem !== undefined || d.BonDedRem !== undefined ||
           d.BinOop !== undefined || d.BinOopRem !== undefined ||
           d.BinCopay1 !== undefined;
  }

  getOtherParsedFieldsObj(): any {
    if (!this.parsedOutput) return {};
    const handled = new Set([
      'CarrierCode', 'CarrierName', 'CarrierAddress1', 'CarrierCity',
      'CarrierState', 'CarrierZip', 'Dtps', 'MemberId',
      'SubscriberRelationship', 'Subscriber', 'Patient',
      'FileStatus', 'FileStatusDescription', 'Plans', 'Benefits',
      'OtherInsurance', 'MedicaidProvidersOther', 'ClaimsInfo',
      'PlanName', 'HasMedicarePassportadvantage'
    ]);
    const result: any = {};
    Object.keys(this.parsedOutput).forEach(k => {
      if (!handled.has(k)) result[k] = this.parsedOutput[k];
    });
    const itNone = this.parsedOutput.Benefits?.['It-none'];
    if (itNone) result['Provider / Other (It-none)'] = itNone;
    return result;
  }

  hasOtherParsedFields(): boolean {
    return Object.keys(this.getOtherParsedFieldsObj()).length > 0;
  }

  getFormattedJson(): string {
    return JSON.stringify(this.waystarResponse, null, 2);
  }

  private fmtCurrency(val: any): string {
    if (val === null || val === undefined || val === '') return '-';
    const n = parseFloat(val);
    if (isNaN(n)) return String(val);
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  private fmtCoIns(rawShare: any, fallbackCovg: any): string {
    if (rawShare !== undefined && rawShare !== null && rawShare !== '') {
      const patientPct = Math.round(parseFloat(rawShare) * 100);
      return `${100 - patientPct}% Plan / ${patientPct}% Patient`;
    }
    return fallbackCovg || '-';
  }
}
